import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, FlaskConical, Loader2, X, FunctionSquare } from "lucide-react";

const inp = "h-9 w-full rounded-lg border border-sky bg-white px-2.5 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20";
const lbl = "mb-1 block text-[11px] font-semibold uppercase tracking-wide text-ink/50";

const empty = { label: "", description: "", entity_type: "salesperson", expression: "", unit: "score", output_type: "score" };

export default function CustomMetricsPanel({ meta, metrics, onChanged }) {
  const [editing, setEditing] = useState(null); // metric object or {} for new

  const del = async (id) => {
    if (!window.confirm("Delete this custom metric?")) return;
    try { await api.delete(`/scoring/custom-metrics/${id}`); toast.success("Deleted"); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="custom-metrics-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm text-ink/60">Build reusable formulas from base metrics (e.g. <code className="rounded bg-mist px-1">contacted/assigned*100</code>) to use in any scoring component.</p>
        <button onClick={() => setEditing({ ...empty })} data-testid="custom-metric-add" className="flex items-center gap-1.5 rounded-lg bg-loop px-3.5 py-2 text-sm font-semibold text-white hover:bg-loophover"><Plus className="h-4 w-4" />New metric</button>
      </div>

      {(metrics || []).length === 0 ? (
        <div className="rounded-2xl border border-dashed border-sky bg-white p-8 text-center text-sm text-ink/50" data-testid="custom-metrics-empty">
          <FunctionSquare className="mx-auto mb-2 h-6 w-6 text-ink/30" />
          No custom metrics yet. Create one to extend the base metric catalog.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {metrics.map((m) => (
            <div key={m.id} data-testid={`custom-metric-${m.key}`} className="rounded-2xl border border-sky/70 bg-white p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h4 className="font-sora text-sm font-semibold text-navy">{m.label}</h4>
                  <span className="text-[10px] font-semibold uppercase text-ink/40">{meta?.entities?.[m.entity_type] || m.entity_type} · {m.unit}</span>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setEditing(m)} className="rounded-lg p-1.5 text-ink/40 hover:bg-loop/5 hover:text-loop"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(m.id)} data-testid={`custom-metric-delete-${m.key}`} className="rounded-lg p-1.5 text-ink/40 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <code className="mt-2 block rounded-lg bg-mist px-2 py-1.5 text-xs text-ink/70">{m.expression}</code>
              {(m.unknown_refs || []).length > 0 && <p className="mt-1.5 text-[11px] text-amber-600">Unknown refs: {m.unknown_refs.join(", ")}</p>}
              {m.description && <p className="mt-1.5 text-xs text-ink/50">{m.description}</p>}
            </div>
          ))}
        </div>
      )}

      {editing && <MetricModal meta={meta} metric={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged(); }} />}
    </div>
  );
}

function MetricModal({ meta, metric, onClose, onSaved }) {
  const [m, setM] = useState({ ...empty, ...metric });
  const [saving, setSaving] = useState(false);
  const [test, setTest] = useState(null);
  const set = (k, v) => setM((s) => ({ ...s, [k]: v }));
  const baseMetrics = meta?.base_metrics?.[m.entity_type] || [];

  const runTest = async () => {
    try {
      const { data } = await api.post("/scoring/formula/test", { expression: m.expression, context: {} });
      if (data.success) {
        const refs = data.data.refs;
        // build a sample context of 1s for preview
        const ctx = Object.fromEntries(refs.map((r) => [r, 1]));
        const { data: d2 } = await api.post("/scoring/formula/test", { expression: m.expression, context: ctx });
        setTest({ ok: true, refs, result: d2.data.result });
      } else setTest({ ok: false, msg: data.message });
    } catch (e) { setTest({ ok: false, msg: apiError(e) }); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { label: m.label, description: m.description || "", entity_type: m.entity_type, expression: m.expression, unit: m.unit || "score", output_type: m.output_type || "score" };
      if (m.id) await api.put(`/scoring/custom-metrics/${m.id}`, body);
      else await api.post("/scoring/custom-metrics", body);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4" onMouseDown={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl" data-testid="custom-metric-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-sora text-lg font-bold text-navy">{m.id ? "Edit" : "New"} custom metric</h3>
          <button onClick={onClose} className="rounded-lg p-1 text-ink/40 hover:bg-mist"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div><label className={lbl}>Label</label><input data-testid="custom-metric-label" className={inp} value={m.label} onChange={(e) => set("label", e.target.value)} /></div>
            <div><label className={lbl}>Applies to</label>
              <select className={inp} value={m.entity_type} disabled={!!m.id} onChange={(e) => set("entity_type", e.target.value)}>
                {Object.entries(meta?.entities || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div><label className={lbl}>Output type</label>
              <select data-testid="custom-metric-output" className={inp} value={m.output_type || "score"} onChange={(e) => set("output_type", e.target.value)}>
                {["count", "percentage", "score", "boolean"].map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>
          <div><label className={lbl}>Formula</label>
            <textarea data-testid="custom-metric-expression" className={`${inp} h-auto py-2 font-mono`} rows={2} placeholder="e.g. contacted/assigned*100" value={m.expression} onChange={(e) => { set("expression", e.target.value); setTest(null); }} />
            <p className="mt-1 text-[11px] text-ink/40">Operators: + − × ÷ % ** · functions: min, max, abs, round</p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {baseMetrics.map((bm) => (
              <button key={bm.key} type="button" onClick={() => set("expression", (m.expression || "") + bm.key)} className="rounded-full border border-sky bg-mist/40 px-2 py-0.5 text-[11px] text-ink/60 hover:border-loop hover:text-loop">{bm.key}</button>
            ))}
          </div>
          <div><label className={lbl}>Description</label><input className={inp} value={m.description || ""} onChange={(e) => set("description", e.target.value)} /></div>
          <div className="flex items-center gap-2">
            <button onClick={runTest} data-testid="custom-metric-test" className="flex items-center gap-1.5 rounded-lg border border-sky px-3 py-1.5 text-xs font-semibold text-ink/70 hover:border-loop hover:text-loop"><FlaskConical className="h-3.5 w-3.5" />Test formula</button>
            {test && (test.ok
              ? <span className="text-xs text-emerald-700" data-testid="custom-metric-test-ok">Valid · refs: {test.refs.join(", ") || "none"} · all-1 → {test.result}</span>
              : <span className="text-xs text-red-600" data-testid="custom-metric-test-err">{test.msg}</span>)}
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-sky px-4 py-2 text-sm font-semibold text-ink/70 hover:bg-mist">Cancel</button>
          <button onClick={save} disabled={saving || !m.label || !m.expression} data-testid="custom-metric-save" className="flex items-center gap-1.5 rounded-lg bg-loop px-4 py-2 text-sm font-semibold text-white hover:bg-loophover disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Save metric
          </button>
        </div>
      </div>
    </div>
  );
}
